import { useState, useRef, useEffect } from 'react'
import { Card, Button } from '@trafiflow/shared-ui'
import {
  RefreshCw,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  Image,
  Film,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Zap,
  Copy,
  Check,
  ScrollText,
  MoreVertical,
  Play,
  Send,
  Bookmark,
  Plus,
  Trash2,
  Download,
  Database,
  ArrowRight,
  X,
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../../../shared/lib/api'
import { AxiosError } from 'axios'

// --- Helpers ---

function extractErrorMessage(err: unknown): string {
  if (err instanceof AxiosError && err.response?.data) {
    const data = err.response.data
    // Try common error field names (message first - it has the details)
    if (typeof data === 'string') return data
    if (data.message) return data.message
    if (data.detail) return data.detail
    if (data.error && data.error !== 'Internal Server Error') return data.error
    // Fallback to status text
    return `${err.response.status}: ${err.response.statusText}`
  }
  if (err instanceof Error) return err.message
  return 'Unknown error'
}

// --- Types ---

interface VideoStatus {
  id: string
  title: string
  status: string
  aiAnalysisStatus: string
  cleanStatus: string
  hasThumbnail: boolean
  createdAt: string
  updatedAt: string
  s3Key: string
  cleanVersionS3Key: string
  thumbnailS3Key: string
}

interface VideoStatusResponse {
  videos: VideoStatus[]
  bucketName: string
  summary: {
    total: number
    aiPending: number
    aiProcessing: number
    aiReady: number
    aiFailed: number
    cleanPending: number
    cleanProcessing: number
    cleanReady: number
    cleanFailed: number
    withThumbnail: number
    withoutThumbnail: number
  }
}

type JobType = 'analyze_video' | 'analyze_video_scenes' | 'clean_video' | 'generate_thumbnail'
type RunMode = 'queue' | 'direct'

interface PersistedLog {
  videoId: string
  videoTitle: string
  job: string
  mode: string
  status: string
  error?: string
  durationMs?: number
  createdAt: string
}

// --- Import Ads Types ---

interface ImportAdsResponse {
  status: string
  pageId: string
  adsScraped: number
  videoAdsFound: number
  videosImported: number
  videosSkipped: number
  videosFailed: number
  importedVideos: { adId: string; videoId: string; title: string }[]
  failedImports: { adId: string; error: string }[]
  durationMs: number
}

interface TriggerETLResponse {
  status: string
  taskArn: string
  message: string
}

interface ETLStatusResponse {
  taskArn: string
  status: string // PROVISIONING, PENDING, RUNNING, STOPPED, COMPLETED, FAILED
  lastStatus: string
  desiredStatus: string
  stoppedReason?: string
  startedAt?: string
  stoppedAt?: string
  message: string
}

// --- Saved Pages ---

interface SavedPage {
  id: string
  name: string
  pageId: string
  createdAt: string
}

const SAVED_PAGES_KEY = 'trafiflow_saved_fb_pages'

function getSavedPages(): SavedPage[] {
  try {
    const stored = localStorage.getItem(SAVED_PAGES_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function savePage(page: Omit<SavedPage, 'id' | 'createdAt'>): SavedPage {
  const pages = getSavedPages()
  const newPage: SavedPage = {
    id: crypto.randomUUID(),
    name: page.name,
    pageId: page.pageId,
    createdAt: new Date().toISOString(),
  }
  pages.push(newPage)
  localStorage.setItem(SAVED_PAGES_KEY, JSON.stringify(pages))
  return newPage
}

function deleteSavedPage(id: string): void {
  const pages = getSavedPages().filter(p => p.id !== id)
  localStorage.setItem(SAVED_PAGES_KEY, JSON.stringify(pages))
}

// --- Progress Steps ---

type ImportStep = 'idle' | 'connecting' | 'fetching' | 'downloading' | 'saving' | 'done' | 'error'

const IMPORT_STEPS: { key: ImportStep; label: string; description: string }[] = [
  { key: 'connecting', label: 'Connecting', description: 'Connecting to API...' },
  { key: 'fetching', label: 'Fetching Ads', description: 'Fetching ads from cache...' },
  { key: 'downloading', label: 'Downloading', description: 'Downloading video files...' },
  { key: 'saving', label: 'Saving', description: 'Saving to Company Videos...' },
  { key: 'done', label: 'Complete', description: 'Import complete!' },
]

// --- API calls ---

async function getVideoStatus() {
  const { data } = await apiClient.get('/debug/videos')
  return data as VideoStatusResponse
}

async function importAdsFromMeta(pageId?: string, limit?: number, createSceneClips?: boolean) {
  const { data } = await apiClient.post('/debug/import-ads', { pageId, limit, createSceneClips }, { timeout: 5 * 60 * 1000 })
  return data as ImportAdsResponse
}

async function triggerETL(pageId: string, limit?: number) {
  const { data } = await apiClient.post('/debug/trigger-etl', { pageId, limit }, { timeout: 30 * 1000 })
  return data as TriggerETLResponse
}

async function getETLStatus(taskArn: string) {
  const { data } = await apiClient.post('/debug/etl-status', { taskArn }, { timeout: 10 * 1000 })
  return data as ETLStatusResponse
}

interface CleanupResponse {
  status: string
  videosKept: number
  videosDeleted: number
  message: string
}

async function cleanupDuplicates() {
  const { data } = await apiClient.post('/debug/cleanup-duplicates', {}, { timeout: 60 * 1000 })
  return data as CleanupResponse
}

async function getProcessingLogs() {
  const { data } = await apiClient.get('/debug/logs')
  return (data as { logs: PersistedLog[] }).logs
}

async function queueJob(videoId: string, job: JobType) {
  const endpoint =
    job === 'analyze_video' ? `/company-videos/${videoId}/reanalyze`
    : job === 'clean_video' ? `/company-videos/${videoId}/re-clean`
    : `/company-videos/${videoId}/re-thumbnail`
  await apiClient.post(endpoint)
}

async function runJobDirectly(videoId: string, job: JobType) {
  const { data } = await apiClient.post('/debug/run-job', { videoId, job }, { timeout: 10 * 60 * 1000 })
  return data as { status: string; error?: string; durationMs?: number }
}

// --- Small Components ---

type StatusKey = 'ready' | 'processing' | 'pending' | 'failed'

function StatusBadge({ status }: { status: string }) {
  const config: Record<StatusKey, { icon: React.ReactNode; cls: string }> = {
    ready:      { icon: <CheckCircle className="w-3 h-3" />, cls: 'bg-green-100 text-green-700 border-green-200' },
    processing: { icon: <Loader2 className="w-3 h-3 animate-spin" />, cls: 'bg-blue-100 text-blue-700 border-blue-200' },
    pending:    { icon: <Clock className="w-3 h-3" />, cls: 'bg-gray-100 text-gray-600 border-gray-200' },
    failed:     { icon: <AlertCircle className="w-3 h-3" />, cls: 'bg-red-100 text-red-700 border-red-200' },
  }
  const { icon, cls } = config[status as StatusKey] ?? config.pending
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>{icon}{status}</span>
}

function StatPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex items-center justify-between py-1 px-2 rounded-md bg-tf-bg-muted/60">
      <span className="text-xs text-tf-text-muted">{label}</span>
      <span className={`text-sm font-bold tabular-nums ${color}`}>{value}</span>
    </div>
  )
}

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
      className="text-tf-text-muted hover:text-tf-text transition-colors p-0.5 rounded" title="Copy">
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
    </button>
  )
}

function S3Row({ label, value, bucket }: { label: string; value: string; bucket?: string }) {
  const fullUrl = value && bucket ? `s3://${bucket}/${value}` : value
  return (
    <div className="flex items-start gap-2">
      <span className="text-xs font-medium text-tf-text-muted w-28 shrink-0 pt-0.5">{label}</span>
      {value
        ? <div className="flex items-center gap-1 min-w-0"><span className="text-xs font-mono text-tf-text truncate" title={fullUrl}>{value}</span><CopyBtn text={fullUrl} /></div>
        : <span className="text-xs text-tf-text-muted italic">--</span>}
    </div>
  )
}

const JOB_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  analyze_video:        { label: 'AI Analysis',    icon: <Zap className="w-3 h-3" />,      color: 'text-blue-600' },
  analyze_video_scenes: { label: 'Scene Clips',    icon: <Film className="w-3 h-3" />,     color: 'text-green-600' },
  clean_video:          { label: 'Clean Video',    icon: <RotateCcw className="w-3 h-3" />, color: 'text-purple-600' },
  generate_thumbnail:   { label: 'Thumbnail',      icon: <Image className="w-3 h-3" />,     color: 'text-amber-600' },
}

// --- Dropdown ---

function ActionDropdown({ video, onRunJob, disabled }: {
  video: VideoStatus; onRunJob: (id: string, job: JobType, mode: RunMode) => void; disabled: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h)
  }, [])

  const jobs: { job: JobType; label: string }[] = [
    { job: 'analyze_video', label: 'AI Analysis' },
    { job: 'analyze_video_scenes', label: 'Scene Clips (create clips)' },
    { job: 'clean_video', label: 'Clean Video' },
    { job: 'generate_thumbnail', label: 'Thumbnail' },
  ]

  return (
    <div ref={ref} className="relative" onClick={e => e.stopPropagation()}>
      <button onClick={() => setOpen(p => !p)} disabled={disabled}
        className="p-1.5 rounded border border-tf-border text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-muted disabled:opacity-40 transition-colors">
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-tf-border z-50 py-1">
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-tf-text-muted">Queue to SQS</p>
          {jobs.map(({ job, label }) => (
            <button key={`q-${job}`} onClick={() => { onRunJob(video.id, job, 'queue'); setOpen(false) }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-tf-text hover:bg-tf-bg-muted transition-colors">
              <Send className="w-3 h-3 text-tf-text-muted" />{label}
            </button>
          ))}
          <div className="border-t border-tf-border my-1" />
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-tf-text-muted">Run Directly (no SQS)</p>
          {jobs.map(({ job, label }) => (
            <button key={`d-${job}`} onClick={() => { onRunJob(video.id, job, 'direct'); setOpen(false) }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-tf-text hover:bg-tf-bg-muted transition-colors">
              <Play className="w-3 h-3 text-green-600" />{label}
              <span className="ml-auto text-[10px] text-tf-text-muted">sync</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// --- Video Row ---

type FilterType = 'all' | 'pending' | 'processing' | 'failed'

function VideoRow({ video, onRunJob, activeJobVideoId, bucketName }: {
  video: VideoStatus; onRunJob: (id: string, job: JobType, mode: RunMode) => void; activeJobVideoId: string | null; bucketName?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const hasIssues = video.aiAnalysisStatus !== 'ready' || video.cleanStatus !== 'ready' || !video.hasThumbnail
  const isActive = activeJobVideoId === video.id
  const fmt = (d: string) => { try { return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) } catch { return d } }

  return (
    <>
      <tr className={`hover:bg-tf-bg-muted/40 transition-colors cursor-pointer ${expanded ? 'bg-tf-bg-muted/20' : ''}`} onClick={() => setExpanded(p => !p)}>
        <td className="px-4 py-3 w-5">{expanded ? <ChevronDown className="w-3.5 h-3.5 text-tf-text-muted" /> : <ChevronRight className="w-3.5 h-3.5 text-tf-text-muted" />}</td>
        <td className="px-3 py-3">
          <div className="flex items-center gap-2">
            {hasIssues && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />}
            <div className="min-w-0">
              <p className="font-medium text-tf-text text-sm truncate max-w-[220px]" title={video.title}>{video.title || 'Untitled'}</p>
              <p className="text-xs text-tf-text-muted font-mono truncate max-w-[220px]" title={video.id}>{video.id}</p>
            </div>
          </div>
        </td>
        <td className="px-3 py-3"><StatusBadge status={video.aiAnalysisStatus} /></td>
        <td className="px-3 py-3"><StatusBadge status={video.cleanStatus} /></td>
        <td className="px-3 py-3">{video.hasThumbnail ? <CheckCircle className="w-4 h-4 text-green-500" /> : <AlertCircle className="w-4 h-4 text-amber-400" />}</td>
        <td className="px-3 py-3 text-tf-text-muted text-xs whitespace-nowrap">{fmt(video.createdAt)}</td>
        <td className="px-3 py-3 text-tf-text-muted text-xs whitespace-nowrap">{fmt(video.updatedAt)}</td>
        <td className="px-3 py-3"><ActionDropdown video={video} onRunJob={onRunJob} disabled={isActive} /></td>
      </tr>
      {expanded && (
        <tr className="bg-tf-bg-muted/10">
          <td colSpan={8} className="px-4 pb-4 pt-1">
            <div className="ml-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-2">S3 Keys</p>
                <S3Row label="Original" value={video.s3Key} bucket={bucketName} />
                <S3Row label="Clean Version" value={video.cleanVersionS3Key} bucket={bucketName} />
                <S3Row label="Thumbnail" value={video.thumbnailS3Key} bucket={bucketName} />
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-2">Diagnostics</p>
                <div className="text-xs space-y-1 text-tf-text-muted">
                  {video.aiAnalysisStatus === 'pending' && <p className="flex items-center gap-1.5 text-amber-600"><AlertCircle className="w-3.5 h-3.5 shrink-0" />AI pending -- use "Run Directly" if SQS worker is off</p>}
                  {video.aiAnalysisStatus === 'failed' && <p className="flex items-center gap-1.5 text-red-600"><AlertCircle className="w-3.5 h-3.5 shrink-0" />AI analysis failed -- retry from dropdown</p>}
                  {video.cleanStatus === 'pending' && !video.cleanVersionS3Key && <p className="flex items-center gap-1.5 text-amber-600"><AlertCircle className="w-3.5 h-3.5 shrink-0" />Clean pending -- worker may be down or ffmpeg missing</p>}
                  {video.cleanStatus === 'failed' && <p className="flex items-center gap-1.5 text-red-600"><AlertCircle className="w-3.5 h-3.5 shrink-0" />Clean failed -- retry from dropdown</p>}
                  {!video.hasThumbnail && <p className="flex items-center gap-1.5 text-amber-600"><AlertCircle className="w-3.5 h-3.5 shrink-0" />No thumbnail -- generate from dropdown</p>}
                  {video.cleanStatus === 'ready' && video.aiAnalysisStatus === 'ready' && video.hasThumbnail && <p className="flex items-center gap-1.5 text-green-600"><CheckCircle className="w-3.5 h-3.5 shrink-0" />All processing complete</p>}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// --- Processing Logs (persistent) ---

function ProcessingLogs({ persistedLogs, isLoading, onRefresh }: {
  persistedLogs: PersistedLog[]; isLoading: boolean; onRefresh: () => void
}) {
  const [expanded, setExpanded] = useState(true)
  const fmtTime = (d: string) => { try { return new Date(d).toLocaleString('en-US', { hour12: false, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) } catch { return d } }

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setExpanded(p => !p)} className="flex items-center gap-2 text-sm font-semibold text-tf-text hover:opacity-80 transition-opacity">
          <ScrollText className="w-4 h-4" />
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          Processing Log
          <span className="text-xs font-normal text-tf-text-muted">({persistedLogs.length} saved)</span>
        </button>
        <Button onClick={onRefresh} disabled={isLoading} variant="secondary" size="sm">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>
      {expanded && (
        <div className="overflow-auto max-h-72 rounded-lg border border-tf-border">
          {persistedLogs.length === 0 ? (
            <p className="text-sm text-tf-text-muted p-4 text-center">No processing logs yet. Run a job to see logs here.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-tf-bg-muted z-10">
                <tr className="text-left text-tf-text-muted">
                  <th className="px-3 py-2">Time</th>
                  <th className="px-3 py-2">Job</th>
                  <th className="px-3 py-2">Mode</th>
                  <th className="px-3 py-2">Video</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Duration</th>
                  <th className="px-3 py-2">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tf-border">
                {persistedLogs.map((log, i) => {
                  const meta = JOB_META[log.job] ?? { label: log.job, icon: null, color: 'text-tf-text' }
                  return (
                    <tr key={i} className="hover:bg-tf-bg-muted/50 transition-colors">
                      <td className="px-3 py-1.5 text-tf-text-muted tabular-nums font-mono whitespace-nowrap">{fmtTime(log.createdAt)}</td>
                      <td className="px-3 py-1.5"><span className={`inline-flex items-center gap-1 font-medium ${meta.color}`}>{meta.icon}{meta.label}</span></td>
                      <td className="px-3 py-1.5">
                        <span className={`inline-flex items-center gap-1 ${log.mode === 'direct' ? 'text-green-600' : 'text-tf-text-muted'}`}>
                          {log.mode === 'direct' ? <Play className="w-3 h-3" /> : <Send className="w-3 h-3" />}
                          {log.mode === 'direct' ? 'Direct' : 'SQS'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-tf-text truncate max-w-[160px]" title={log.videoTitle}>{log.videoTitle || log.videoId}</td>
                      <td className="px-3 py-1.5">
                        {log.status === 'completed' && <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle className="w-3 h-3" />Done</span>}
                        {log.status === 'failed' && <span className="inline-flex items-center gap-1 text-red-600"><AlertCircle className="w-3 h-3" />Failed</span>}
                        {log.status === 'queued' && <span className="inline-flex items-center gap-1 text-blue-600"><Send className="w-3 h-3" />Queued</span>}
                      </td>
                      <td className="px-3 py-1.5 text-tf-text-muted tabular-nums">{log.durationMs != null ? `${(log.durationMs / 1000).toFixed(1)}s` : '--'}</td>
                      <td className="px-3 py-1.5 text-tf-text-muted truncate max-w-[200px]" title={log.error}>{log.error || (log.status === 'completed' ? 'Success' : '--')}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  )
}

// --- Progress Indicator ---

function ImportProgress({ currentStep, error }: { currentStep: ImportStep; error?: string | null }) {
  if (currentStep === 'idle') return null

  const stepIndex = IMPORT_STEPS.findIndex(s => s.key === currentStep)
  const isError = currentStep === 'error'
  const isDone = currentStep === 'done'

  const getStepIcon = (_step: typeof IMPORT_STEPS[number], idx: number) => {
    if (isError && idx === stepIndex) return <AlertCircle className="w-4 h-4 text-red-500" />
    if (idx < stepIndex || isDone) return <CheckCircle className="w-4 h-4 text-green-500" />
    if (idx === stepIndex) return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
    return <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
  }

  const getStepIconComponent = (key: ImportStep) => {
    switch (key) {
      case 'connecting': return <Send className="w-3 h-3" />
      case 'fetching': return <Database className="w-3 h-3" />
      case 'downloading': return <Download className="w-3 h-3" />
      case 'saving': return <Database className="w-3 h-3" />
      case 'done': return <CheckCircle className="w-3 h-3" />
      default: return null
    }
  }

  return (
    <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
      <div className="flex items-center gap-2 mb-3">
        <Loader2 className={`w-4 h-4 ${isDone ? 'text-green-600' : 'text-blue-600 animate-spin'}`} />
        <span className={`text-sm font-medium ${isDone ? 'text-green-700' : isError ? 'text-red-700' : 'text-blue-700'}`}>
          {isDone ? 'Import Complete!' : isError ? 'Import Failed' : 'Importing...'}
        </span>
      </div>

      <div className="space-y-2">
        {IMPORT_STEPS.map((step, idx) => {
          const isCurrent = idx === stepIndex
          const isPast = idx < stepIndex || isDone

          return (
            <div
              key={step.key}
              className={`flex items-center gap-3 p-2 rounded-md transition-all ${
                isCurrent && !isError ? 'bg-white/80 shadow-sm' : ''
              } ${isError && isCurrent ? 'bg-red-100/80' : ''}`}
            >
              {getStepIcon(step, idx)}
              <span className={`flex items-center gap-1.5 text-xs font-medium ${
                isPast ? 'text-green-600' :
                isCurrent && !isError ? 'text-blue-600' :
                isCurrent && isError ? 'text-red-600' :
                'text-gray-400'
              }`}>
                {getStepIconComponent(step.key)}
                {step.label}
              </span>
              {isCurrent && !isError && !isDone && (
                <span className="text-xs text-blue-500 ml-auto">{step.description}</span>
              )}
              {isPast && idx < IMPORT_STEPS.length - 1 && (
                <ArrowRight className="w-3 h-3 text-green-400 ml-auto" />
              )}
            </div>
          )
        })}
      </div>

      {isError && error && (
        <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}

// --- Saved Pages Manager ---

function SavedPagesManager({
  savedPages,
  onSelect,
  onDelete,
  onRefresh,
}: {
  savedPages: SavedPage[]
  onSelect: (pageId: string) => void
  onDelete: (id: string) => void
  onRefresh: () => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (savedPages.length === 0) return null

  return (
    <div className="border border-tf-border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(p => !p)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-tf-bg-muted/50 hover:bg-tf-bg-muted transition-colors"
      >
        <Bookmark className="w-3.5 h-3.5 text-blue-500" />
        <span className="text-xs font-medium text-tf-text">Saved Pages</span>
        <span className="text-xs text-tf-text-muted">({savedPages.length})</span>
        {isExpanded ? <ChevronDown className="w-3 h-3 ml-auto text-tf-text-muted" /> : <ChevronRight className="w-3 h-3 ml-auto text-tf-text-muted" />}
      </button>
      {isExpanded && (
        <div className="divide-y divide-tf-border">
          {savedPages.map(page => (
            <div key={page.id} className="flex items-center gap-2 px-3 py-2 hover:bg-tf-bg-muted/30 transition-colors group">
              <button
                onClick={() => onSelect(page.pageId)}
                className="flex-1 text-left"
              >
                <span className="text-sm font-medium text-tf-text">{page.name}</span>
                <span className="text-xs text-tf-text-muted ml-2 font-mono">{page.pageId}</span>
              </button>
              <button
                onClick={() => { onDelete(page.id); onRefresh() }}
                className="p-1 text-tf-text-muted hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                title="Delete saved page"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// --- Import Ads Panel ---

function ImportAdsPanel({ onImportComplete }: { onImportComplete: () => void }) {
  const [pageId, setPageId] = useState('')
  const [pageName, setPageName] = useState('')
  const [limit, setLimit] = useState(20)
  const [createSceneClips, setCreateSceneClips] = useState(true) // Default to true for scene-based clipping
  const [isImporting, setIsImporting] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isCleaning, setIsCleaning] = useState(false)
  const [cleanupResult, setCleanupResult] = useState<CleanupResponse | null>(null)
  const [result, setResult] = useState<ImportAdsResponse | null>(null)
  const [etlResult, setEtlResult] = useState<TriggerETLResponse | null>(null)
  const [etlStatus, setEtlStatus] = useState<ETLStatusResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(true)
  const [savedPages, setSavedPages] = useState<SavedPage[]>([])
  const [showSaveForm, setShowSaveForm] = useState(false)
  const [currentStep, setCurrentStep] = useState<ImportStep>('idle')

  // Load saved pages on mount
  useEffect(() => {
    setSavedPages(getSavedPages())
  }, [])

  // Poll ETL status when task is running
  useEffect(() => {
    if (!etlResult?.taskArn) return

    // Don't poll if already completed or failed
    if (etlStatus?.status === 'COMPLETED' || etlStatus?.status === 'FAILED') return

    const pollStatus = async () => {
      try {
        const status = await getETLStatus(etlResult.taskArn)
        setEtlStatus(status)

        // Stop polling if task is done
        if (status.status === 'COMPLETED' || status.status === 'FAILED' || status.status === 'STOPPED') {
          return
        }
      } catch {
        // Silently fail - ETL status checking requires ECS access which may not be available locally
      }
    }

    // Initial fetch
    pollStatus()

    // Poll every 10 seconds (less aggressive)
    const interval = setInterval(pollStatus, 10000)
    return () => clearInterval(interval)
  }, [etlResult?.taskArn, etlStatus?.status])

  const refreshSavedPages = () => {
    setSavedPages(getSavedPages())
  }

  const handleSavePage = () => {
    if (!pageId || !pageName.trim()) return
    savePage({ name: pageName.trim(), pageId })
    setPageName('')
    setShowSaveForm(false)
    refreshSavedPages()
  }

  const handleSelectSavedPage = (selectedPageId: string) => {
    setPageId(selectedPageId)
  }

  const simulateProgress = async () => {
    // Simulate progress through steps with realistic timing
    const steps: ImportStep[] = ['connecting', 'fetching', 'downloading', 'saving']
    for (const step of steps) {
      setCurrentStep(step)
      // Vary wait times based on step
      const delay = step === 'downloading' ? 2000 : step === 'saving' ? 1500 : 800
      await new Promise(r => setTimeout(r, delay))
    }
  }

  const handleImport = async () => {
    setIsImporting(true)
    setError(null)
    setResult(null)
    setEtlResult(null)
    setCurrentStep('connecting')

    // Start progress simulation in parallel
    const progressPromise = simulateProgress()

    try {
      const response = await importAdsFromMeta(pageId || undefined, limit, createSceneClips)
      await progressPromise // Wait for progress animation to catch up
      setCurrentStep('done')
      setResult(response)
      onImportComplete()

      // Reset progress after a delay
      setTimeout(() => setCurrentStep('idle'), 3000)
    } catch (err) {
      setCurrentStep('error')
      setError(extractErrorMessage(err))
    } finally {
      setIsImporting(false)
    }
  }

  const handleRefreshCache = async () => {
    if (!pageId) {
      setError('Please enter a Facebook Page ID first')
      return
    }
    setIsRefreshing(true)
    setError(null)
    setResult(null)
    setEtlResult(null)
    setEtlStatus(null) // Reset status for new task
    try {
      const response = await triggerETL(pageId, limit)
      setEtlResult(response)
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleCleanupDuplicates = async () => {
    setIsCleaning(true)
    setCleanupResult(null)
    setError(null)
    try {
      const response = await cleanupDuplicates()
      setCleanupResult(response)
      onImportComplete() // Refresh the video list
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setIsCleaning(false)
    }
  }

  return (
    <Card className="p-4">
      <button
        onClick={() => setExpanded(p => !p)}
        className="flex items-center gap-2 text-sm font-semibold text-tf-text hover:opacity-80 transition-opacity w-full"
      >
        <Film className="w-4 h-4" />
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        Import Ads from Meta
        <span className="text-xs font-normal text-tf-text-muted ml-2">Pull video ads → Company Videos</span>
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Saved Pages */}
          <SavedPagesManager
            savedPages={savedPages}
            onSelect={handleSelectSavedPage}
            onDelete={deleteSavedPage}
            onRefresh={refreshSavedPages}
          />

          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-tf-text-muted mb-1.5">
                Facebook Page ID <span className="text-amber-600">(required if no Meta connected)</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={pageId}
                  onChange={e => setPageId(e.target.value)}
                  placeholder="e.g., 606426623024865 (Shapermint)"
                  className="flex-1 px-3 py-2 text-sm border border-tf-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
                {pageId && !savedPages.some(p => p.pageId === pageId) && (
                  <button
                    onClick={() => setShowSaveForm(p => !p)}
                    className={`p-2 rounded-md border transition-colors ${showSaveForm ? 'bg-blue-50 border-blue-300 text-blue-600' : 'border-tf-border text-tf-text-muted hover:text-blue-600 hover:border-blue-300'}`}
                    title="Save this page"
                  >
                    <Bookmark className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-[10px] text-tf-text-muted mt-1">
                Find Page ID: Go to Facebook Ad Library → Search for a brand → Copy the page_id from URL
              </p>
            </div>
            <div className="w-24">
              <label className="block text-xs font-medium text-tf-text-muted mb-1.5">Limit</label>
              <input
                type="number"
                value={limit}
                onChange={e => setLimit(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
                min={1}
                max={50}
                className="w-full px-3 py-2 text-sm border border-tf-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={createSceneClips}
                  onChange={e => setCreateSceneClips(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
              <span className="text-xs text-tf-text-muted" title="Analyze video for scenes and create individual clips for each scene">
                Create Clips
              </span>
            </div>
            <Button onClick={handleImport} disabled={isImporting || isRefreshing} variant="primary" size="sm">
              {isImporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Import Video Ads
                </>
              )}
            </Button>
            <Button onClick={handleRefreshCache} disabled={isRefreshing || isImporting || !pageId} variant="secondary" size="sm" title="Refresh cached ads with fresh URLs (runs Fargate ETL)">
              {isRefreshing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Refresh Cache
                </>
              )}
            </Button>
            <Button onClick={handleCleanupDuplicates} disabled={isCleaning || isImporting} variant="secondary" size="sm" title="Remove duplicate imported videos">
              {isCleaning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cleaning...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Cleanup Duplicates
                </>
              )}
            </Button>
          </div>

          {/* Cleanup Result */}
          {cleanupResult && (
            <div className="p-3 rounded-md bg-green-50 border border-green-200">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                <Check className="w-4 h-4" />
                Cleanup Complete
              </div>
              <div className="mt-2 text-xs text-green-600">
                Deleted {cleanupResult.videosDeleted} duplicates, kept {cleanupResult.videosKept} unique videos
              </div>
            </div>
          )}

          {/* Save Page Form */}
          {showSaveForm && pageId && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-blue-50 border border-blue-200">
              <Bookmark className="w-4 h-4 text-blue-500 shrink-0" />
              <input
                type="text"
                value={pageName}
                onChange={e => setPageName(e.target.value)}
                placeholder="Enter a name (e.g., Nike, Shapermint)"
                className="flex-1 px-2 py-1.5 text-sm border border-blue-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                onKeyDown={e => e.key === 'Enter' && handleSavePage()}
              />
              <Button onClick={handleSavePage} disabled={!pageName.trim()} variant="primary" size="sm">
                <Plus className="w-3.5 h-3.5 mr-1" />
                Save
              </Button>
              <button onClick={() => setShowSaveForm(false)} className="p-1 text-tf-text-muted hover:text-tf-text">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Progress Indicator */}
          <ImportProgress currentStep={currentStep} error={error} />

          {etlResult && (
            <div className={`p-3 rounded-md space-y-2 ${
              etlStatus?.status === 'COMPLETED' ? 'bg-green-50 border border-green-200' :
              etlStatus?.status === 'FAILED' ? 'bg-red-50 border border-red-200' :
              'bg-blue-50 border border-blue-200'
            }`}>
              <div className={`flex items-center gap-2 text-sm font-medium ${
                etlStatus?.status === 'COMPLETED' ? 'text-green-700' :
                etlStatus?.status === 'FAILED' ? 'text-red-700' :
                'text-blue-700'
              }`}>
                {etlStatus?.status === 'COMPLETED' ? (
                  <CheckCircle className="w-4 h-4" />
                ) : etlStatus?.status === 'FAILED' ? (
                  <AlertCircle className="w-4 h-4" />
                ) : etlStatus?.status === 'RUNNING' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4" />
                )}
                {etlStatus?.status === 'COMPLETED' ? 'ETL Completed!' :
                 etlStatus?.status === 'FAILED' ? 'ETL Failed' :
                 etlStatus?.status === 'RUNNING' ? 'ETL Running...' :
                 'ETL Task Started'}
              </div>
              <p className={`text-xs ${
                etlStatus?.status === 'COMPLETED' ? 'text-green-600' :
                etlStatus?.status === 'FAILED' ? 'text-red-600' :
                'text-blue-600'
              }`}>
                {etlStatus?.message || etlResult.message}
              </p>
              <div className="flex items-center gap-2 text-[10px] text-tf-text-muted">
                <span className={`px-1.5 py-0.5 rounded font-medium ${
                  etlStatus?.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                  etlStatus?.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                  etlStatus?.status === 'RUNNING' ? 'bg-blue-100 text-blue-700' :
                  etlStatus?.status === 'PENDING' || etlStatus?.status === 'PROVISIONING' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {etlStatus?.status || 'STARTING'}
                </span>
                {etlStatus?.startedAt && (
                  <span>Started: {new Date(etlStatus.startedAt).toLocaleTimeString()}</span>
                )}
                {etlStatus?.stoppedAt && (
                  <span>Stopped: {new Date(etlStatus.stoppedAt).toLocaleTimeString()}</span>
                )}
              </div>
              <p className="text-[10px] text-tf-text-muted font-mono break-all">Task: {etlResult.taskArn}</p>
              {etlStatus?.status === 'COMPLETED' && (
                <p className="text-xs text-green-600 font-medium mt-2">
                  Cache refreshed! Click "Import Video Ads" to import with fresh URLs.
                </p>
              )}
              {etlStatus?.stoppedReason && etlStatus?.status === 'FAILED' && (
                <p className="text-xs text-red-600 mt-1">
                  Reason: {etlStatus.stoppedReason}
                </p>
              )}
            </div>
          )}

          {error && currentStep !== 'error' && (
            <div className="p-3 rounded-md bg-red-50 border border-red-200">
              <p className="text-sm text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </p>
            </div>
          )}

          {result && (
            <div className="p-3 rounded-md bg-green-50 border border-green-200 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                <CheckCircle className="w-4 h-4" />
                Import Complete
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                <div className="bg-white/60 rounded px-2 py-1">
                  <span className="text-tf-text-muted">Ads Scraped:</span>
                  <span className="ml-1 font-semibold text-tf-text">{result.adsScraped}</span>
                </div>
                <div className="bg-white/60 rounded px-2 py-1">
                  <span className="text-tf-text-muted">Video Ads:</span>
                  <span className="ml-1 font-semibold text-tf-text">{result.videoAdsFound}</span>
                </div>
                <div className="bg-white/60 rounded px-2 py-1">
                  <span className="text-tf-text-muted">Imported:</span>
                  <span className="ml-1 font-semibold text-green-600">{result.videosImported}</span>
                </div>
                {result.videosSkipped > 0 && (
                  <div className="bg-white/60 rounded px-2 py-1">
                    <span className="text-tf-text-muted">Skipped:</span>
                    <span className="ml-1 font-semibold text-amber-600">{result.videosSkipped}</span>
                  </div>
                )}
                <div className="bg-white/60 rounded px-2 py-1">
                  <span className="text-tf-text-muted">Duration:</span>
                  <span className="ml-1 font-semibold text-tf-text">{(result.durationMs / 1000).toFixed(1)}s</span>
                </div>
              </div>
              {result.failedImports && result.failedImports.length > 0 && (
                <div className="text-xs text-amber-600">
                  {result.failedImports.length} failed: {result.failedImports.map(f => f.error).join(', ')}
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-tf-text-muted">
            This will scrape ads from the Facebook Ad Library, download video ads, and save them to your Company Videos.
            Each video will be queued for AI analysis, thumbnail generation, and clean version creation.
          </p>
        </div>
      )}
    </Card>
  )
}

// --- Main ---

export function VideoStatusDebug() {
  const [filter, setFilter] = useState<FilterType>('all')
  const [activeJobVideoId, setActiveJobVideoId] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data, isLoading, error, refetch } = useQuery<VideoStatusResponse>({
    queryKey: ['debug', 'videos'],
    queryFn: getVideoStatus,
    refetchInterval: 10000,
  })

  const { data: persistedLogs = [], isLoading: logsLoading, refetch: refetchLogs } = useQuery<PersistedLog[]>({
    queryKey: ['debug', 'logs'],
    queryFn: getProcessingLogs,
    refetchInterval: 30000,
  })

  const handleRunJob = async (videoId: string, job: JobType, mode: RunMode) => {
    setActiveJobVideoId(videoId)
    try {
      if (mode === 'queue') {
        await queueJob(videoId, job)
      } else {
        await runJobDirectly(videoId, job)
      }
    } catch (err) {
      console.error('[VideoStatusDebug] Job failed:', err)
    } finally {
      setActiveJobVideoId(null)
      queryClient.invalidateQueries({ queryKey: ['debug', 'videos'] })
      queryClient.invalidateQueries({ queryKey: ['debug', 'logs'] })
    }
  }

  const filteredVideos = (data?.videos ?? []).filter(v => {
    if (filter === 'all') return true
    if (filter === 'pending') return v.aiAnalysisStatus === 'pending' || v.cleanStatus === 'pending'
    if (filter === 'processing') return v.aiAnalysisStatus === 'processing' || v.cleanStatus === 'processing'
    if (filter === 'failed') return v.aiAnalysisStatus === 'failed' || v.cleanStatus === 'failed'
    return true
  })

  const counts: Record<FilterType, number> = {
    all: data?.videos.length ?? 0,
    pending: (data?.videos ?? []).filter(v => v.aiAnalysisStatus === 'pending' || v.cleanStatus === 'pending').length,
    processing: (data?.videos ?? []).filter(v => v.aiAnalysisStatus === 'processing' || v.cleanStatus === 'processing').length,
    failed: (data?.videos ?? []).filter(v => v.aiAnalysisStatus === 'failed' || v.cleanStatus === 'failed').length,
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-tf-text">Video Status Debug</h1>
          <p className="text-xs text-tf-text-muted mt-0.5">Auto-refreshes every 10s</p>
        </div>
        <Button onClick={() => refetch()} disabled={isLoading} variant="secondary" size="sm">
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      {error && <Card className="p-4 border-red-200 bg-red-50"><p className="text-red-700 text-sm">Error: {(error as Error).message}</p></Card>}

      <ImportAdsPanel onImportComplete={() => refetch()} />

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card className="p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-tf-text-muted mb-3 flex items-center gap-1.5"><Film className="w-3.5 h-3.5" />Total</h3>
              <p className="text-3xl font-bold text-tf-text tabular-nums">{data.summary.total}</p>
            </Card>
            <Card className="p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-tf-text-muted mb-3 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" />AI Analysis</h3>
              <div className="space-y-1"><StatPill label="Ready" value={data.summary.aiReady} color="text-green-600" /><StatPill label="Processing" value={data.summary.aiProcessing} color="text-blue-600" /><StatPill label="Pending" value={data.summary.aiPending} color="text-gray-500" /><StatPill label="Failed" value={data.summary.aiFailed} color="text-red-600" /></div>
            </Card>
            <Card className="p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-tf-text-muted mb-3 flex items-center gap-1.5"><Film className="w-3.5 h-3.5" />Clean</h3>
              <div className="space-y-1"><StatPill label="Ready" value={data.summary.cleanReady} color="text-green-600" /><StatPill label="Processing" value={data.summary.cleanProcessing} color="text-blue-600" /><StatPill label="Pending" value={data.summary.cleanPending} color="text-gray-500" /><StatPill label="Failed" value={data.summary.cleanFailed} color="text-red-600" /></div>
            </Card>
            <Card className="p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-tf-text-muted mb-3 flex items-center gap-1.5"><Image className="w-3.5 h-3.5" />Thumbnails</h3>
              <div className="space-y-1"><StatPill label="Has" value={data.summary.withThumbnail} color="text-green-600" /><StatPill label="Missing" value={data.summary.withoutThumbnail} color="text-amber-500" /></div>
            </Card>
          </div>

          <div className="flex gap-1.5 border-b border-tf-border pb-0">
            {(['all', 'pending', 'processing', 'failed'] as FilterType[]).map(f => (
              <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-xs font-medium rounded-t-md border-b-2 -mb-px transition-colors ${filter === f ? 'border-blue-500 text-blue-600' : 'border-transparent text-tf-text-muted hover:text-tf-text hover:border-tf-border'}`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-semibold ${filter === f ? 'bg-blue-100 text-blue-600' : 'bg-tf-bg-muted text-tf-text-muted'}`}>{counts[f]}</span>
              </button>
            ))}
          </div>

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-tf-bg-muted border-b border-tf-border">
                  <tr className="text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                    <th className="px-4 py-2.5 w-5" /><th className="px-3 py-2.5">Video</th><th className="px-3 py-2.5">AI</th><th className="px-3 py-2.5">Clean</th><th className="px-3 py-2.5">Thumb</th><th className="px-3 py-2.5">Created</th><th className="px-3 py-2.5">Updated</th><th className="px-3 py-2.5 w-12" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-tf-border">
                  {filteredVideos.length === 0
                    ? <tr><td colSpan={8} className="px-4 py-10 text-center text-tf-text-muted text-sm">No videos match the current filter</td></tr>
                    : filteredVideos.map(v => <VideoRow key={v.id} video={v} onRunJob={handleRunJob} activeJobVideoId={activeJobVideoId} bucketName={data.bucketName} />)}
                </tbody>
              </table>
            </div>
          </Card>

          <ProcessingLogs persistedLogs={persistedLogs} isLoading={logsLoading} onRefresh={() => refetchLogs()} />

          <Card className="p-4">
            <p className="text-xs font-semibold text-tf-text-muted uppercase tracking-wider mb-3">How It Works</p>
            <div className="text-xs text-tf-text-muted space-y-1.5">
              <p><Send className="w-3 h-3 inline mr-1" /><span className="font-medium">Queue to SQS</span> -- sends to SQS. Needs <code className="bg-tf-bg-muted px-1 py-0.5 rounded">pnpm dev</code> (starts worker automatically).</p>
              <p><Play className="w-3 h-3 inline mr-1 text-green-600" /><span className="font-medium text-green-600">Run Directly</span> -- runs in the API process. No worker needed. AI may take 2-5 min.</p>
            </div>
          </Card>
        </>
      )}
    </div>
  )
}
